import { Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { getAdminLinkedinSessionMonitor } from 'API/linkedin-funnel';
import AdminSubpageHeader from 'features/admin/admin-subpage-header';
import { useUserData } from 'hooks/use-user-data';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { Roles, Routes } from '../enums';

type LinkedInSessionMonitor = {
  windowHours: number;
  generatedAt: number;
  totals: {
    failedJobsInWindow: number;
    sessionExpiryIncidents: number;
    affectedAccounts: number;
  };
  severity: 'ok' | 'warning' | 'critical';
  topAccounts: Array<{ accountId: string; failures: number }>;
  latestIncidents: Array<{
    id: string;
    failedAt: number;
    failedReason: string;
    accountId?: string;
    campaignId?: string;
  }>;
};

const AdminLinkedinSessionMonitorPage: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useUserData();
  const [monitor, setMonitor] = useState<LinkedInSessionMonitor | null>(null);
  const [loadingMonitor, setLoadingMonitor] = useState(false);
  const [monitorError, setMonitorError] = useState<string | null>(null);

  const fetchMonitor = useCallback(async () => {
    setLoadingMonitor(true);
    setMonitorError(null);
    try {
      const data = await getAdminLinkedinSessionMonitor(24);
      setMonitor(data || null);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setMonitorError(err?.message || 'Could not load monitor data');
    } finally {
      setLoadingMonitor(false);
    }
  }, []);

  useEffect(() => {
    if (role === Roles.OWNER) {
      void fetchMonitor();
    }
  }, [role, fetchMonitor]);

  const severityColor = useMemo(() => {
    if (!monitor) return 'default' as const;
    if (monitor.severity === 'critical') return 'error' as const;
    if (monitor.severity === 'warning') return 'warning' as const;
    return 'success' as const;
  }, [monitor]);

  if (role !== Roles.OWNER) {
    return (
      <div className={classes.page}>
        <Typography>{t('noPermission', 'You do not have permission to access this page.')}</Typography>
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <AdminSubpageHeader title={t('adminNavLinkedinSession24h')} />
      <Paper className={classes.card} elevation={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1} flexWrap="wrap" gap={1}>
          <Typography className={classes.title}>{t('adminLinkedinSessionPageTitle', 'LinkedIn Session Monitor (24h)')}</Typography>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Button size="small" variant="outlined" onClick={() => navigate(Routes.ADMIN_MONITORING)}>
              {t('adminOpenMonitoringDetails', 'Open monitoring details')}
            </Button>
            <Chip size="small" color={severityColor} label={monitor ? monitor.severity.toUpperCase() : 'UNKNOWN'} />
            <Button size="small" variant="outlined" onClick={fetchMonitor} disabled={loadingMonitor}>
              {t('refresh', 'Refresh')}
            </Button>
          </Stack>
        </Stack>

        {loadingMonitor ? <LinearProgress /> : null}
        {monitorError ? <Typography color="error">{monitorError}</Typography> : null}

        {monitor ? (
          <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap mt={1}>
            <Chip label={`Session incidents: ${monitor.totals.sessionExpiryIncidents}`} />
            <Chip label={`Affected accounts: ${monitor.totals.affectedAccounts}`} />
            <Chip label={`Failed jobs total: ${monitor.totals.failedJobsInWindow}`} />
            <Chip
              label={`Latest: ${monitor.latestIncidents[0] ? new Date(monitor.latestIncidents[0].failedAt).toLocaleString() : '-'}`}
            />
          </Stack>
        ) : null}

        <Box mt={1}>
          <Typography className={classes.hint}>
            {t(
              'adminLinkedinSessionHint',
              'This summary tracks search-worker failures in the last 24 hours and highlights session-expiry patterns.',
            )}
          </Typography>
        </Box>

        {monitor?.latestIncidents?.length ? (
          <Box mt={1.5}>
            <Typography className={classes.listTitle}>{t('adminLatestIncidents', 'Latest 10 incidents')}</Typography>
            <Stack gap={0.75} mt={0.75}>
              {monitor.latestIncidents.slice(0, 10).map((incident) => (
                <Paper key={incident.id} variant="outlined" className={classes.incidentRow}>
                  <Typography className={classes.incidentText}>
                    {new Date(incident.failedAt).toLocaleString()} | account: {incident.accountId || '-'} | campaign:{' '}
                    {incident.campaignId || '-'}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Paper>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  page: {
    padding: '20px 16px 16px 48px',
    [theme.breakpoints.down('lg')]: {
      padding: '20px 8px 8px 8px',
    },
  },
  card: {
    border: `1px solid ${theme.palette.greys.light}`,
    borderRadius: 8,
    padding: 12,
    maxWidth: 900,
  },
  title: {
    fontWeight: 600,
  },
  hint: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(12),
  },
  listTitle: {
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(13),
  },
  incidentRow: {
    padding: '6px 8px',
  },
  incidentText: {
    fontSize: theme.typography.pxToRem(12),
    fontFamily: 'monospace',
  },
}));

export default AdminLinkedinSessionMonitorPage;
